import { useEffect, useMemo, useState } from 'react';

import { Box } from '@mui/material';

import { SPONSOR_TIER_DURATION } from '../../constants';
import { SponsorLevel, sponsors } from '../../data/sponsors';

interface RotatingSponsorBannerProps {
  sectionKey: string;
}

function hashString(value: string) {
  return value
    .split('')
    .reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0, 0);
}

export function RotatingSponsorBanner({
  sectionKey,
}: RotatingSponsorBannerProps) {
  const ANIMATION_MS = 700;

  const bannerSponsors = useMemo(
    () =>
      sponsors.filter(
        (sponsor) =>
          sponsor.level === SponsorLevel.GOLD ||
          sponsor.level === SponsorLevel.SILVER ||
          sponsor.level === SponsorLevel.BRONZE,
      ),
    [],
  );

  const initialIndex = useMemo(() => {
    if (!bannerSponsors.length) return 0;
    return hashString(sectionKey) % bannerSponsors.length;
  }, [bannerSponsors, sectionKey]);

  const [index, setIndex] = useState(initialIndex);
  const [previousIndex, setPreviousIndex] = useState<number | null>(null);

  useEffect(() => {
    setIndex(initialIndex);
    setPreviousIndex(null);
  }, [initialIndex]);

  useEffect(() => {
    const current = bannerSponsors[index];

    if (!current) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setPreviousIndex(index);
      setIndex((prev) => (prev + 1) % bannerSponsors.length);
    }, SPONSOR_TIER_DURATION[current.level]);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [bannerSponsors, index]);

  useEffect(() => {
    if (previousIndex === null) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setPreviousIndex(null);
    }, ANIMATION_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [ANIMATION_MS, previousIndex]);

  const currentSponsor = bannerSponsors[index];
  const previousSponsor =
    previousIndex !== null ? bannerSponsors[previousIndex] : null;

  if (!currentSponsor) {
    return null;
  }

  function renderSlide(
    sponsor: (typeof bannerSponsors)[number],
    mode: 'active' | 'leaving',
  ) {
    return (
      <Box
        key={`${sponsor.name}-${mode}`}
        sx={{
          position: 'absolute',
          inset: 0,
          animation:
            mode === 'active'
              ? `sponsorSlideIn ${ANIMATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`
              : `sponsorSlideOut ${ANIMATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
        }}>
        <Box
          component="img"
          src={sponsor.bannerImageUrl ?? sponsor.imageUrl}
          alt={sponsor.name}
          sx={{
            width: '100%',
            height: { xs: 104, sm: 140, md: 240, lg: 272 },
            objectFit: 'cover',
            display: 'block',
            backgroundColor: '#111',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.08) 45%, rgba(0,0,0,0.28) 100%)',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      component="a"
      href={currentSponsor.site}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`acessar patrocinador ${currentSponsor.name}`}
      sx={{
        height: { xs: 104, sm: 140, md: 240, lg: 272 },
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        display: 'block',
        textDecoration: 'none',
        border: '1px solid rgba(255,255,255,0.08)',
        '@keyframes sponsorSlideIn': {
          '0%': { transform: 'translateX(100%)', opacity: 0.72 },
          '100%': { transform: 'translateX(0%)', opacity: 1 },
        },
        '@keyframes sponsorSlideOut': {
          '0%': { transform: 'translateX(0%)', opacity: 1 },
          '100%': { transform: 'translateX(-100%)', opacity: 0.72 },
        },
        transition: 'transform 0.2s ease-in-out, border-color 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: 'rgba(255,130,17,0.7)',
        },
      }}>
      {previousSponsor && renderSlide(previousSponsor, 'leaving')}
      {renderSlide(currentSponsor, 'active')}

      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 10,
          transform: 'translateY(-50%)',
          flexShrink: 0,
          px: 1.25,
          py: 0.5,
          borderRadius: '0 0 0 4px',
          bgcolor: 'rgba(255,130,17,0.9)',
          color: 'white',
          fontSize: { xs: '0.62rem', md: '0.72rem' },
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          zIndex: 2,
        }}>
        Publicidade
      </Box>
    </Box>
  );
}
